import React from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { gql } from 'apollo-boost'
import { useApolloClient } from '@apollo/react-hooks'
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import { Alert, ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import { useHistory, useLocation } from 'react-router-dom'
import { AI_SERVICE_BASE_URL } from '../../env'

const PERSON_EMAIL_QUERY = gql`
  query AIToolingSessionsListPersonEmail($personId: String!) {
    person(id: $personId) {
      id
      account {
        emailAddress
      }
    }
  }
`

interface PersonEmailQueryData {
  person: {
    id: string
    account: {
      emailAddress: string | null
    } | null
  } | null
}

interface PersonEmailQueryVariables {
  personId: string
}

const SESSION_TYPES = [
  'assistant_message_timing',
  'assistant_message_extraction',
  'assistant_message_generation',
  'assistant_web_search',
  'message_generation_agent',
  'channel_guide_chat',
  'onboarding_infer_persona_context',
  'onboarding_infer_board_location_context',
  'onboarding_prioritize_message_examples',
  'news_headline_summary',
  'tag_generation',
  'benchmark_model_oneshot',
  'benchmark_agent_tool_eval',
  'benchmark_agent_product_eval',
  'benchmark_agent_cache',
  'script_bedrock_converse'
] as const

type SessionTypeEnum = (typeof SESSION_TYPES)[number]

const SESSION_TYPE_META: Record<
  SessionTypeEnum,
  {
    label: string
    description: string
  }
> = {
  assistant_message_timing: {
    label: 'Assistant Message Timing',
    description: 'Resolves the requested message delivery time.'
  },
  assistant_message_extraction: {
    label: 'Assistant Message Extraction',
    description: 'Extracts the message text from an Assistant request.'
  },
  assistant_message_generation: {
    label: 'Assistant Message Generation',
    description: 'Generates message candidates for the selected Vestaboard.'
  },
  assistant_web_search: {
    label: 'Assistant Web Search',
    description: 'Luna web research with search counts and cost estimates.'
  },
  message_generation_agent: {
    label: 'Message Generation Agent',
    description: 'V2 message generation in native installables.'
  },
  channel_guide_chat: {
    label: 'Channel Guide Chat',
    description: 'Onboarding flow that helps with channel recommendations.'
  },
  onboarding_infer_persona_context: {
    label: 'Persona Context',
    description: 'Onboarding: maps user input to a persona profile.'
  },
  onboarding_infer_board_location_context: {
    label: 'Board Location',
    description: 'Onboarding: tunes home vs. work (or similar) board context.'
  },
  onboarding_prioritize_message_examples: {
    label: 'Prioritize Message Examples',
    description: 'Onboarding: ranks examples by relevance to what the user has entered so far.'
  },
  news_headline_summary: {
    label: 'News Headline Summary',
    description: 'Summarizes news headlines into Vestaboard note lines (system sessions).'
  },
  tag_generation: {
    label: 'Tag Generation',
    description: 'Internal: adds text to messages.'
  },
  benchmark_model_oneshot: {
    label: 'Benchmark: Model Oneshot',
    description: 'Internal benchmark run for testing.'
  },
  benchmark_agent_tool_eval: {
    label: 'Benchmark: Agent Tool Eval',
    description: 'Internal benchmark run for testing.'
  },
  benchmark_agent_product_eval: {
    label: 'Benchmark: Agent Product Eval',
    description: 'Evaluates agent behavior across product scenarios.'
  },
  benchmark_agent_cache: {
    label: 'Benchmark: Agent Cache',
    description: 'Internal benchmark run for testing.'
  },
  script_bedrock_converse: {
    label: 'Script: Bedrock Converse',
    description: 'Internal script for testing (Bedrock Converse).'
  }
}

function titleCaseFromSnakeCase(type: string): string {
  return type
    .split('_')
    .map(part => (part ? part[0].toUpperCase() + part.slice(1).toLowerCase() : ''))
    .join(' ')
}

function getSessionTypeLabel(type: string): string {
  if (SESSION_TYPE_META[type as SessionTypeEnum]) {
    return SESSION_TYPE_META[type as SessionTypeEnum].label
  }
  return titleCaseFromSnakeCase(type)
}

/** Normalized slug used for API query (lowercase snake_case segments). */
const SESSION_TYPE_SLUG_RE = /^[a-z0-9_]+$/

function normalizeSessionTypeSlugInput(raw: string): string {
  return raw.trim().toLowerCase()
}

function isValidSessionTypeSlugNormalized(slug: string): boolean {
  return slug.length > 0 && SESSION_TYPE_SLUG_RE.test(slug)
}

type ListMode = 'person' | 'type'

type RangePreset = '5m' | '1h' | '1d' | '1w' | 'custom'

interface SessionSummary {
  totalEstimatedCostUsd: string
  userIntentSummary: string
  /** Finished primary model invocations (user-facing turns), when the rollup is present. */
  turnCount?: number
}

interface SessionRow {
  sessionId: string
  type: string
  personId: string
  createdAt: number
  summary?: SessionSummary | null
}

interface SessionsListResponse {
  data: SessionRow[]
  nextCursor: string | null
}

const useStyles = makeStyles(theme => ({
  pageContainer: {
    paddingBottom: theme.spacing(4)
  },
  sectionDescription: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary
  },
  filterCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginTop: theme.spacing(1)
  },
  loaderWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 240,
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  tableWrap: {
    overflowX: 'auto'
  },
  resultsSummaryRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  resultsSummaryCount: {
    flex: '1 1 auto',
    minWidth: 0
  },
  resultsSummaryActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0
  },
  topLoadMoreEndText: {
    color: theme.palette.text.disabled
  },
  rowNumberHead: {
    width: 48,
    paddingRight: theme.spacing(1)
  },
  rowNumberCell: {
    width: 48,
    paddingRight: theme.spacing(1),
    textAlign: 'right',
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
    userSelect: 'none'
  },
  mono: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: 13
  },
  loadMoreWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2)
  },
  loadMoreEndState: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    color: theme.palette.text.disabled
  },
  selectPlaceholder: {
    color: theme.palette.text.secondary
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  sessionTypeSourceRow: {
    marginBottom: theme.spacing(1)
  },
  sessionTypeSelect: {
    minWidth: 320
  },
  sessionTypeMenuItem: {
    alignItems: 'flex-start',
    whiteSpace: 'normal' as const
  },
  sessionTypeMenuLabel: {
    fontWeight: 500,
    lineHeight: 1.35
  },
  sessionTypeMenuDescription: {
    display: 'block',
    marginTop: 4,
    lineHeight: 1.35
  },
  personEmail: {
    fontWeight: 500,
    lineHeight: 1.3,
    wordBreak: 'break-word' as const
  },
  personIdSmall: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: 11,
    marginTop: 2,
    color: theme.palette.text.secondary,
    wordBreak: 'break-all' as const
  },
  personIdOnly: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: 13
  },
  intentSummaryCell: {
    maxWidth: 360,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
    lineHeight: 1.35
  },
  tabularCell: {
    fontVariantNumeric: 'tabular-nums'
  }
}))

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function isValidPersonId(value: string): boolean {
  const t = value.trim()
  if (t === 'system') {
    return true
  }
  return UUID_RE.test(t)
}

function presetToRangeSeconds(preset: RangePreset): { from: number; to: number } {
  const to = Math.floor(Date.now() / 1000)
  const minute = 60
  const hour = 60 * minute
  const day = 24 * hour
  const deltas: Record<Exclude<RangePreset, 'custom'>, number> = {
    '5m': 5 * minute,
    '1h': hour,
    '1d': day,
    '1w': 7 * day
  }
  if (preset === 'custom') {
    return { from: to - day, to }
  }
  return { from: to - deltas[preset], to }
}

function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Parses datetime-local strings to Unix seconds; start must be before end. */
function customDatetimeRangeToSeconds(
  customFrom: string,
  customTo: string
): { from: number; to: number } | null {
  if (!customFrom || !customTo) {
    return null
  }
  const fromMs = new Date(customFrom).getTime()
  const toMs = new Date(customTo).getTime()
  if (Number.isNaN(fromMs) || Number.isNaN(toMs) || fromMs > toMs) {
    return null
  }
  return {
    from: Math.floor(fromMs / 1000),
    to: Math.floor(toMs / 1000)
  }
}

interface SessionsListUrlState {
  mode: ListMode
  personId: string
  sessionType: '' | SessionTypeEnum
  sessionTypeSource: 'preset' | 'custom'
  customSessionTypeSlug: string
  rangePreset: RangePreset
  /** Snapshot for non-custom presets so the URL can freeze from/to (not recomputed from Date.now()). */
  rangeBoundsSec: { from: number; to: number } | null
  customFrom: string
  customTo: string
}

/** Defaults for filter state; only a subset is read/written on the query string (not `rangePreset`). */
const SESSION_LIST_DEFAULTS: SessionsListUrlState = {
  mode: 'type',
  personId: '',
  sessionType: '',
  sessionTypeSource: 'preset',
  customSessionTypeSlug: '',
  /** Which preset toggle is selected when the URL has no `from`/`to` (UI only). */
  rangePreset: '1d',
  rangeBoundsSec: null,
  customFrom: '',
  customTo: ''
}

function parseSessionsListSearch(search: string): SessionsListUrlState {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const modeRaw = params.get('mode')
  const mode: ListMode =
    modeRaw === 'person' || modeRaw === 'type' ? modeRaw : SESSION_LIST_DEFAULTS.mode

  const stsRaw = params.get('sessionTypeSource')
  let sessionTypeSource: 'preset' | 'custom' =
    stsRaw === 'custom' ? 'custom' : stsRaw === 'preset' ? 'preset' : SESSION_LIST_DEFAULTS.sessionTypeSource

  const sessionTypeParam = params.get('sessionType')?.trim() ?? ''
  let sessionType: '' | SessionTypeEnum = ''
  let customSessionTypeSlug = ''

  if (mode === 'type') {
    if (sessionTypeSource === 'custom') {
      customSessionTypeSlug = sessionTypeParam
    } else if (sessionTypeParam !== '') {
      if ((SESSION_TYPES as readonly string[]).includes(sessionTypeParam)) {
        sessionType = sessionTypeParam as SessionTypeEnum
      } else if (isValidSessionTypeSlugNormalized(normalizeSessionTypeSlugInput(sessionTypeParam))) {
        sessionTypeSource = 'custom'
        customSessionTypeSlug = sessionTypeParam
      }
    }
  }

  const fromSec = params.get('from')
  const toSec = params.get('to')
  const fromN = fromSec != null ? Number.parseInt(fromSec, 10) : Number.NaN
  const toN = toSec != null ? Number.parseInt(toSec, 10) : Number.NaN
  const hasValidBounds = !Number.isNaN(fromN) && !Number.isNaN(toN) && fromN <= toN

  let rangePreset: RangePreset = SESSION_LIST_DEFAULTS.rangePreset
  let customFrom = ''
  let customTo = ''

  if (hasValidBounds) {
    // Absolute window in the query string → Custom time range UI.
    rangePreset = 'custom'
    customFrom = toDatetimeLocalValue(new Date(fromN * 1000))
    customTo = toDatetimeLocalValue(new Date(toN * 1000))
  }

  return {
    mode,
    personId: params.get('personId')?.trim() ?? '',
    sessionType,
    sessionTypeSource,
    customSessionTypeSlug,
    rangePreset,
    // Preset snapshot is filled by the seed effect when there is no from/to in the URL.
    rangeBoundsSec: null,
    customFrom,
    customTo
  }
}

function buildSessionsListSearch(state: SessionsListUrlState): string {
  const params = new URLSearchParams()

  if (state.mode !== SESSION_LIST_DEFAULTS.mode) {
    params.set('mode', state.mode)
  }

  if (state.mode === 'person' && state.personId.trim() !== '') {
    params.set('personId', state.personId.trim())
  }

  if (state.mode === 'type') {
    if (state.sessionTypeSource === 'custom') {
      params.set('sessionTypeSource', 'custom')
      const normalized = normalizeSessionTypeSlugInput(state.customSessionTypeSlug)
      if (normalized !== '') {
        params.set('sessionType', normalized)
      }
    } else if (state.sessionType !== '') {
      params.set('sessionType', state.sessionType)
    }
  }

  if (state.rangePreset === 'custom') {
    const secs = customDatetimeRangeToSeconds(state.customFrom, state.customTo)
    if (secs) {
      params.set('from', String(secs.from))
      params.set('to', String(secs.to))
    }
  } else if (state.rangeBoundsSec) {
    params.set('from', String(state.rangeBoundsSec.from))
    params.set('to', String(state.rangeBoundsSec.to))
  }

  return params.toString()
}

function normalizeQueryKeyOrder(search: string): string {
  const raw = search.startsWith('?') ? search.slice(1) : search
  const p = new URLSearchParams(raw)
  return Array.from(p.entries())
    .sort(([ka], [kb]) => ka.localeCompare(kb))
    .map(([k, v]) => `${k}=${v}`)
    .join('&')
}

function searchQueriesEquivalent(a: string, b: string): boolean {
  return normalizeQueryKeyOrder(a) === normalizeQueryKeyOrder(b)
}

function formatSessionTimeMs(ms: number): string {
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) {
    return String(ms)
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(d)
}

/** USD numeric string → cents, with four decimal places (fractional cents). */
function formatUsdToCents(usd: string | undefined): string {
  if (usd == null || usd === '') {
    return '—'
  }
  const n = Number(usd)
  if (Number.isNaN(n)) {
    return '—'
  }
  return (n * 100).toFixed(4)
}


function uniqueResolvablePersonIds(rows: SessionRow[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const row of rows) {
    const id = row.personId.trim()
    if (!UUID_RE.test(id) || seen.has(id)) {
      continue
    }
    seen.add(id)
    out.push(id)
  }
  return out
}

const SessionPersonColumn: React.FC<{
  personId: string
  email: string | null | undefined
  classes: ReturnType<typeof useStyles>
}> = ({ personId, email, classes }) => {
  const trimmed = personId.trim()
  const showEmail = email != null && email !== ''

  if (showEmail) {
    return (
      <Box>
        <Typography variant='body2' className={classes.personEmail}>
          {email}
        </Typography>
        <Typography variant='caption' component='div' className={classes.personIdSmall}>
          {trimmed}
        </Typography>
      </Box>
    )
  }

  return (
    <Typography variant='body2' component='div' className={classes.personIdOnly}>
      {trimmed}
    </Typography>
  )
}

export const AIToolingSessionsList = () => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const apolloClient = useApolloClient()
  const { getAccessTokenSilently } = useAuth0()

  const [personEmailById, setPersonEmailById] = React.useState<Record<string, string | null>>({})
  const personEmailRequestedRef = React.useRef<Set<string>>(new Set())
  const skipHydrateFromUrlRef = React.useRef(false)

  // Snapshot only for initial state on mount; URL changes are handled by the hydrate effect below.
  const initialFromUrl = React.useMemo(() => parseSessionsListSearch(location.search), []) // eslint-disable-line react-hooks/exhaustive-deps -- mount-only

  const [mode, setMode] = React.useState<ListMode>(() => initialFromUrl.mode)
  const [personId, setPersonId] = React.useState(() => initialFromUrl.personId)
  const [debouncedPersonId, setDebouncedPersonId] = React.useState(() => initialFromUrl.personId.trim())
  const [sessionType, setSessionType] = React.useState<'' | SessionTypeEnum>(() => initialFromUrl.sessionType)
  const [sessionTypeSource, setSessionTypeSource] = React.useState<'preset' | 'custom'>(
    () => initialFromUrl.sessionTypeSource
  )
  const [customSessionTypeSlug, setCustomSessionTypeSlug] = React.useState(() => initialFromUrl.customSessionTypeSlug)
  const [debouncedCustomSessionTypeSlug, setDebouncedCustomSessionTypeSlug] = React.useState(() =>
    normalizeSessionTypeSlugInput(initialFromUrl.customSessionTypeSlug)
  )
  const [rangePreset, setRangePreset] = React.useState<RangePreset>(() => initialFromUrl.rangePreset)
  const [rangeBoundsSec, setRangeBoundsSec] = React.useState<{ from: number; to: number } | null>(
    () => initialFromUrl.rangeBoundsSec
  )
  const [customFrom, setCustomFrom] = React.useState(() => initialFromUrl.customFrom)
  const [customTo, setCustomTo] = React.useState(() => initialFromUrl.customTo)

  const [rows, setRows] = React.useState<SessionRow[]>([])
  const [nextCursor, setNextCursor] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [loadingMore, setLoadingMore] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const initialFetchSeqRef = React.useRef(0)

  React.useEffect(() => {
    if (skipHydrateFromUrlRef.current) {
      skipHydrateFromUrlRef.current = false
      return
    }
    const parsed = parseSessionsListSearch(location.search)
    setMode(parsed.mode)
    setPersonId(parsed.personId)
    setDebouncedPersonId(parsed.personId.trim())
    setSessionType(parsed.sessionType)
    setSessionTypeSource(parsed.sessionTypeSource)
    setCustomSessionTypeSlug(parsed.customSessionTypeSlug)
    setDebouncedCustomSessionTypeSlug(normalizeSessionTypeSlugInput(parsed.customSessionTypeSlug))
    setRangePreset(parsed.rangePreset)
    setRangeBoundsSec(parsed.rangeBoundsSec)
    setCustomFrom(parsed.customFrom)
    setCustomTo(parsed.customTo)
  }, [location.search])

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedPersonId(personId.trim())
    }, 450)
    return () => window.clearTimeout(timer)
  }, [personId])

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedCustomSessionTypeSlug(normalizeSessionTypeSlugInput(customSessionTypeSlug))
    }, 450)
    return () => window.clearTimeout(timer)
  }, [customSessionTypeSlug])

  React.useEffect(() => {
    if (rangePreset !== 'custom') {
      return
    }
    const { from, to } = presetToRangeSeconds('1d')
    const toLocal = new Date(to * 1000)
    const fromLocal = new Date(from * 1000)
    const toStr = toDatetimeLocalValue(toLocal)
    const fromStr = toDatetimeLocalValue(fromLocal)
    setCustomFrom(s => (s ? s : fromStr))
    setCustomTo(s => (s ? s : toStr))
  }, [rangePreset])

  /** First paint for preset ranges: snapshot rolling window once so URL/from/to stay stable. */
  React.useEffect(() => {
    if (rangePreset === 'custom') {
      return
    }
    setRangeBoundsSec(prev => (prev != null ? prev : presetToRangeSeconds(rangePreset)))
  }, [rangePreset])

  const urlStateSnapshot = React.useMemo(
    (): SessionsListUrlState => ({
      mode,
      personId,
      sessionType,
      sessionTypeSource,
      customSessionTypeSlug,
      rangePreset,
      rangeBoundsSec,
      customFrom,
      customTo
    }),
    [
      mode,
      personId,
      sessionType,
      sessionTypeSource,
      customSessionTypeSlug,
      rangePreset,
      rangeBoundsSec,
      customFrom,
      customTo
    ]
  )

  React.useEffect(() => {
    const next = buildSessionsListSearch(urlStateSnapshot)
    const current = location.search.startsWith('?') ? location.search.slice(1) : location.search
    if (searchQueriesEquivalent(next, current)) {
      return
    }
    skipHydrateFromUrlRef.current = true
    history.replace({
      pathname: location.pathname,
      search: next ? `?${next}` : ''
    })
  }, [history, location.pathname, location.search, urlStateSnapshot])

  const getRangeSeconds = React.useCallback((): { from: number; to: number } | null => {
    if (rangePreset === 'custom') {
      return customDatetimeRangeToSeconds(customFrom, customTo)
    }
    return rangeBoundsSec ?? presetToRangeSeconds(rangePreset)
  }, [rangePreset, rangeBoundsSec, customFrom, customTo])

  const effectiveSessionType = React.useMemo(() => {
    if (mode !== 'type') {
      return null
    }
    if (sessionTypeSource === 'preset') {
      return sessionType !== '' ? sessionType : null
    }
    const slug = debouncedCustomSessionTypeSlug
    return isValidSessionTypeSlugNormalized(slug) ? slug : null
  }, [mode, sessionTypeSource, sessionType, debouncedCustomSessionTypeSlug])

  const canFetch = React.useMemo(() => {
    const range = getRangeSeconds()
    if (!range) {
      return false
    }
    if (mode === 'type') {
      return effectiveSessionType !== null
    }
    return isValidPersonId(debouncedPersonId)
  }, [mode, effectiveSessionType, debouncedPersonId, getRangeSeconds])

  const fetchPage = React.useCallback(
    async (cursor: string | null, append: boolean) => {
      if (!AI_SERVICE_BASE_URL) {
        setError('Missing API base URL. Configure AI_SERVICE_ENDPOINT in root env.')
        return
      }

      const range = getRangeSeconds()
      if (!range) {
        setError('Choose a valid date range (custom requires start and end, with start before end).')
        return
      }

      if (mode === 'type' && !effectiveSessionType) {
        return
      }

      if (mode === 'person' && !isValidPersonId(debouncedPersonId)) {
        return
      }

      const seq = append ? 0 : ++initialFetchSeqRef.current

      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
        setError(null)
      }

      try {
        const token = await getAccessTokenSilently()
        const params = new URLSearchParams()
        params.set('from', String(range.from))
        params.set('to', String(range.to))
        if (mode === 'person') {
          params.set('personId', debouncedPersonId.trim())
        } else {
          params.set('sessionType', effectiveSessionType as string)
        }
        params.set('limit', '50')
        if (cursor) {
          params.set('cursor', cursor)
        }

        const response = await fetch(`${AI_SERVICE_BASE_URL}/admin/sessions?${params.toString()}`, {
          headers: {
            'X-Vestaboard-Token': token
          }
        })

        if (!append && seq !== initialFetchSeqRef.current) {
          return
        }

        if (!response.ok) {
          let detail = `Request failed (${response.status})`
          try {
            const errBody = (await response.json()) as { message?: string }
            if (errBody.message) {
              detail = errBody.message
            }
          } catch {
            /* ignore */
          }
          throw new Error(detail)
        }

        const json = (await response.json()) as SessionsListResponse

        if (!append && seq !== initialFetchSeqRef.current) {
          return
        }

        setNextCursor(json.nextCursor)
        if (append) {
          setRows(prev => [...prev, ...json.data])
        } else {
          setRows(json.data)
        }
      } catch (e) {
        if (!append && seq !== initialFetchSeqRef.current) {
          return
        }
        const message = e instanceof Error ? e.message : 'Failed to load sessions.'
        setError(message)
        if (!append) {
          setRows([])
          setNextCursor(null)
        }
      } finally {
        if (append) {
          setLoadingMore(false)
        } else if (!append && seq === initialFetchSeqRef.current) {
          setLoading(false)
        }
      }
    },
    [getAccessTokenSilently, getRangeSeconds, mode, debouncedPersonId, effectiveSessionType]
  )

  React.useEffect(() => {
    if (!canFetch) {
      initialFetchSeqRef.current += 1
      setRows([])
      setNextCursor(null)
      setError(null)
      setLoading(false)
      return
    }
    setNextCursor(null)
    void fetchPage(null, false)
  }, [canFetch, fetchPage])

  const handleLoadMore = () => {
    if (nextCursor) {
      void fetchPage(nextCursor, true)
    }
  }

  const uniquePersonIdsForEmail = React.useMemo(() => uniqueResolvablePersonIds(rows), [rows])

  React.useEffect(() => {
    let cancelled = false

    const idsToResolve = uniquePersonIdsForEmail.filter(id => !personEmailRequestedRef.current.has(id))
    for (const id of idsToResolve) {
      personEmailRequestedRef.current.add(id)
    }

    void Promise.all(
      idsToResolve.map(personId =>
        apolloClient
          .query<PersonEmailQueryData, PersonEmailQueryVariables>({
            query: PERSON_EMAIL_QUERY,
            variables: { personId },
            fetchPolicy: 'cache-first'
          })
          .then(result => {
            if (cancelled) {
              return
            }
            const email = result.data?.person?.account?.emailAddress ?? null
            setPersonEmailById(prev => {
              if (Object.prototype.hasOwnProperty.call(prev, personId)) {
                return prev
              }
              return { ...prev, [personId]: email }
            })
          })
          .catch(() => {
            if (cancelled) {
              return
            }
            setPersonEmailById(prev => {
              if (Object.prototype.hasOwnProperty.call(prev, personId)) {
                return prev
              }
              return { ...prev, [personId]: null }
            })
          })
      )
    )

    return () => {
      cancelled = true
    }
  }, [apolloClient, uniquePersonIdsForEmail])

  const emptyStateMessage = React.useMemo(() => {
    if (!canFetch) {
      if (mode === 'type' && !effectiveSessionType) {
        if (sessionTypeSource === 'preset') {
          return 'Select a session type to load sessions.'
        }
        const raw = customSessionTypeSlug.trim()
        if (!raw) {
          return 'Enter a session type slug to load sessions.'
        }
        const normalized = normalizeSessionTypeSlugInput(customSessionTypeSlug)
        if (!isValidSessionTypeSlugNormalized(normalized)) {
          return 'Use snake_case: letters, numbers, and underscores only (e.g. my_session_type).'
        }
        return 'Sessions load automatically after you pause typing.'
      }
      if (mode === 'person') {
        if (!personId.trim()) {
          return 'Enter a person id (UUID or system). Sessions load automatically after you pause typing.'
        }
        if (!isValidPersonId(debouncedPersonId)) {
          return 'Enter a valid person id (UUID or system).'
        }
      }
      if (rangePreset === 'custom' && !getRangeSeconds()) {
        return 'Set a valid custom start and end time (start must be before end).'
      }
      return 'Adjust filters to load sessions.'
    }
    return 'No sessions match these filters in the selected time range.'
  }, [
    canFetch,
    mode,
    effectiveSessionType,
    sessionTypeSource,
    customSessionTypeSlug,
    personId,
    debouncedPersonId,
    rangePreset,
    getRangeSeconds
  ])

  const renderLoadMoreButton = () => (
    <Button
      variant='outlined'
      color='primary'
      onClick={handleLoadMore}
      disabled={loadingMore}
      startIcon={loadingMore ? <CircularProgress color='inherit' size={18} /> : undefined}>
      Load more
    </Button>
  )

  return (
    <Box className={classes.pageContainer}>
      <Typography variant='h4'>AI Sessions</Typography>
      <Typography variant='body2' className={classes.sectionDescription}>
        List tracked sessions by person id or by session type. Choose filters and time range—results load automatically (person id is debounced). Paginate with Load more.
      </Typography>

      <Card className={classes.filterCard}>
        <Grid container spacing={2} alignItems='flex-end'>
          <Grid item xs={12}>
            <Typography variant='subtitle2' gutterBottom>
              Filter by
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={mode}
              onChange={(_, v: ListMode | null) => {
                if (v) {
                  setMode(v)
                }
              }}
              size='small'>
              <ToggleButton value='type'>Session type</ToggleButton>
              <ToggleButton value='person'>Person id</ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {mode === 'person' ? (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='Person id'
                placeholder='UUID or system'
                value={personId}
                onChange={e => setPersonId(e.target.value)}
                variant='outlined'
                size='small'
              />
              <FormHelperText>Required: a user UUID or the literal system</FormHelperText>
            </Grid>
          ) : (
            <Grid item xs={12} md={6}>
              <Typography variant='subtitle2' gutterBottom>
                Session type
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={sessionTypeSource}
                onChange={(_, v: 'preset' | 'custom' | null) => {
                  if (v) {
                    setSessionTypeSource(v)
                  }
                }}
                size='small'
                className={classes.sessionTypeSourceRow}>
                <ToggleButton value='preset'>Standard list</ToggleButton>
                <ToggleButton value='custom'>Custom slug</ToggleButton>
              </ToggleButtonGroup>
              {sessionTypeSource === 'preset' ? (
                <FormControl variant='outlined' size='small' fullWidth className={classes.sessionTypeSelect}>
                  <InputLabel id='session-type-label' shrink>
                    Type
                  </InputLabel>
                  <Select
                    labelId='session-type-label'
                    label='Type'
                    displayEmpty
                    value={sessionType}
                    onChange={e => setSessionType(e.target.value as SessionTypeEnum | '')}
                    MenuProps={{
                      PaperProps: { style: { maxHeight: 420 } }
                    }}
                    renderValue={selected => {
                      if (selected === '') {
                        return <span className={classes.selectPlaceholder}>Select a session type</span>
                      }
                      return getSessionTypeLabel(selected as SessionTypeEnum)
                    }}>
                    <MenuItem value='' disabled>
                      <em>Select a session type</em>
                    </MenuItem>
                    {SESSION_TYPES.map(t => {
                      const meta = SESSION_TYPE_META[t]
                      return (
                        <MenuItem key={t} value={t} className={classes.sessionTypeMenuItem}>
                          <Box>
                            <Typography variant='body2' component='div' className={classes.sessionTypeMenuLabel}>
                              {meta.label}
                            </Typography>
                            <Typography
                              variant='caption'
                              color='textSecondary'
                              component='div'
                              className={classes.sessionTypeMenuDescription}>
                              {meta.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      )
                    })}
                  </Select>
                </FormControl>
              ) : (
                <>
                  <TextField
                    fullWidth
                    label='Session type slug'
                    placeholder='e.g. my_new_session_type'
                    value={customSessionTypeSlug}
                    onChange={e => setCustomSessionTypeSlug(e.target.value)}
                    variant='outlined'
                    size='small'
                    error={
                      customSessionTypeSlug.trim() !== '' &&
                      !isValidSessionTypeSlugNormalized(normalizeSessionTypeSlugInput(customSessionTypeSlug))
                    }
                  />
                  <FormHelperText>
                    Enter the API session type key (snake_case). Requests use the slug after you pause typing
                    (same debounce as person id).
                  </FormHelperText>
                </>
              )}
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant='subtitle2' gutterBottom>
              Time range
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={rangePreset}
              onChange={(_, v: RangePreset | null) => {
                if (!v) {
                  return
                }
                if (v === 'custom') {
                  const base = rangeBoundsSec ?? presetToRangeSeconds(rangePreset)
                  setCustomFrom(toDatetimeLocalValue(new Date(base.from * 1000)))
                  setCustomTo(toDatetimeLocalValue(new Date(base.to * 1000)))
                  setRangeBoundsSec(null)
                  setRangePreset('custom')
                  return
                }
                setRangeBoundsSec(presetToRangeSeconds(v))
                setRangePreset(v)
              }}
              size='small'>
              <ToggleButton value='5m'>5m</ToggleButton>
              <ToggleButton value='1h'>1h</ToggleButton>
              <ToggleButton value='1d'>1d</ToggleButton>
              <ToggleButton value='1w'>1w</ToggleButton>
              <ToggleButton value='custom'>Custom</ToggleButton>
            </ToggleButtonGroup>
            {rangePreset === 'custom' ? (
              <Box className={classes.chipRow}>
                <TextField
                  label='Start'
                  type='datetime-local'
                  value={customFrom}
                  onChange={e => setCustomFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant='outlined'
                  size='small'
                />
                <TextField
                  label='End'
                  type='datetime-local'
                  value={customTo}
                  onChange={e => setCustomTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant='outlined'
                  size='small'
                />
              </Box>
            ) : null}
          </Grid>
        </Grid>
      </Card>

      {error ? (
        <Alert severity='error' style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box className={classes.loaderWrap}>
          <CircularProgress size={48} />
          <Typography variant='body2' color='textSecondary'>
            Loading sessions…
          </Typography>
        </Box>
      ) : (
        <>
          {rows.length > 0 ? (
            <>
              <Box className={classes.resultsSummaryRow}>
                <Box className={classes.resultsSummaryCount}>
                  <Typography variant='subtitle1' component='div' style={{ margin: 0 }}>
                    <Box component='span' fontWeight={700}>
                      {rows.length}
                    </Box>{' '}
                    {rows.length === 1 ? 'session' : 'sessions'}
                    {nextCursor ? (
                      <Typography component='span' variant='body2' color='textSecondary'>
                        {' '}
                        · more available
                      </Typography>
                    ) : null}
                  </Typography>
                </Box>
                <Box className={classes.resultsSummaryActions}>
                  {nextCursor ? (
                    renderLoadMoreButton()
                  ) : (
                    <Typography variant='body2' className={classes.topLoadMoreEndText}>
                      Everything has been loaded.
                    </Typography>
                  )}
                </Box>
              </Box>
            <div className={classes.tableWrap}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.rowNumberHead}>#</TableCell>
                    <TableCell>Session id</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Person</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align='right'>Est. cost (¢)</TableCell>
                    <TableCell align='right' className={classes.tabularCell}>
                      Turns
                    </TableCell>
                    <TableCell>User intent</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => {
                    const summaryPath = `/ai-tooling/sessions/${encodeURIComponent(row.sessionId)}/summary`
                    const openSummary = (e: React.MouseEvent) => {
                      if (e.metaKey || e.ctrlKey) {
                        window.open(summaryPath, '_blank', 'noopener,noreferrer')
                        return
                      }
                      history.push(summaryPath)
                    }
                    return (
                    <TableRow
                      key={row.sessionId}
                      hover
                      className={classes.clickableRow}
                      onClick={openSummary}>
                      <TableCell className={classes.rowNumberCell}>{index + 1}</TableCell>
                      <TableCell className={classes.mono}>{row.sessionId}</TableCell>
                      <TableCell>
                        <Chip size='small' label={getSessionTypeLabel(row.type)} />
                      </TableCell>
                      <TableCell>
                        <SessionPersonColumn
                          personId={row.personId}
                          email={personEmailById[row.personId.trim()]}
                          classes={classes}
                        />
                      </TableCell>
                      <TableCell>{formatSessionTimeMs(row.createdAt)}</TableCell>
                      <TableCell align='right' className={classes.tabularCell}>
                        {row.summary != null
                          ? formatUsdToCents(row.summary.totalEstimatedCostUsd)
                          : '—'}
                      </TableCell>
                      <TableCell align='right' className={classes.tabularCell}>
                        {row.summary?.turnCount != null ? row.summary.turnCount : '—'}
                      </TableCell>
                      <TableCell className={classes.intentSummaryCell}>
                        <Typography variant='body2' component='span'>
                          {row.summary?.userIntentSummary ?? '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
              {nextCursor ? (
                <Box className={classes.loadMoreWrap}>{renderLoadMoreButton()}</Box>
              ) : (
                <Box className={classes.loadMoreEndState}>
                  <Typography variant='body2'>Everything has been loaded.</Typography>
                </Box>
              )}
            </>
          ) : !error ? (
            <Typography variant='body2' color='textSecondary'>
              {emptyStateMessage}
            </Typography>
          ) : null}
        </>
      )}
    </Box>
  )
}
